import React, {
  useState,
  useContext,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';

import NotesModal from '../../../../components/Leads/components/NotesModal';
import { LeadContractModal } from '../../../../components/ContractModal';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import { StyledTabs } from '../../../MealsPlanner/styles';
import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import UserContext from '../../../../../context/UserContext';
import LeadsContext from '../../../../context/LeadsContext';
import { groupLeadsBySalesStage } from '../../../../utils/lead';

import ISDashboardTabs from './tabs';
import ISDashboardTabsConfig, { isDashboardTabsFilterChecks } from './utils';
import {
  HeaderContainer,
  TabHeaderRow,
  PageContent,
} from './styles';

const LeadsTableSection = ({
  setShowQuestionsModal,
  coachesList,
  selectedLead,
  setSelectedLead,
}) => {
  const [selectedTab, setSelectedTab] = useState(ISDashboardTabs.FOLLOW_UP);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { userDoc } = useContext(UserContext);
  const {
    leads,
    loadingLeads,
    setIsAgentId,
    setIsISDashboardView,
    selectedPeriod,
    setSelectedPeriod,
    paymentPendingLeads,
  } = useContext(LeadsContext);
  const {
    loadNewCoachData = () => {},
  } = useContext(ExternalCoachContext);

  // Loads the inside sales agent's claimed leads data for the IS dashboard view.
  useEffect(() => {
    const init = () => {
      setIsISDashboardView(true);
      loadNewCoachData(Object.keys(coachesList).map((coach) => coach));

      if (userDoc) {
        setIsAgentId(userDoc.id);
      }
      if (!loadingLeads) {
        setIsLoading(false);
      }
    };

    init();
  }, [
    userDoc,
    setIsAgentId,
    loadingLeads,
    loadNewCoachData,
    coachesList,
    setIsISDashboardView,
  ]);

  const [rowsBySalesStage, salesStagesCounters] = useMemo(
    () => groupLeadsBySalesStage(leads, ISDashboardTabs, isDashboardTabsFilterChecks),
    [leads],
  );

  const onOnboardingLinkClick = useCallback(() => {
    setShowContractModal(true);
  }, [setShowContractModal]);

  const onNotesClick = useCallback(() => {
    setShowNotesModal(true);
  }, [setShowNotesModal]);

  const shouldRenderRows = !!rowsBySalesStage[selectedTab];

  // Create a reactive tabs config with current counts for all tabs
  const tabsConfigWithCounts = useMemo(() => {
    const configWithCounts = { ...ISDashboardTabsConfig };

    Object.keys(ISDashboardTabsConfig).forEach((tabKey) => {
      let currentCount = 0;
      if (!loadingLeads) {
        currentCount = tabKey === ISDashboardTabs.PAYMENT_PENDING
          ? paymentPendingLeads.length
          : salesStagesCounters[tabKey] || 0;
      }
      configWithCounts[tabKey] = {
        ...ISDashboardTabsConfig[tabKey],
        count: currentCount,
      };
    });

    return configWithCounts;
  }, [salesStagesCounters, loadingLeads, paymentPendingLeads]);

  const tabContent = useMemo(() => {
    const { component: TabContent, props } = ISDashboardTabsConfig[selectedTab];

    let extraProps = {};
    if (selectedTab === ISDashboardTabs.CONTRACTS_MANAGER) {
      extraProps = {
        isISDashboardView: true,
        userId: userDoc.id,
        userName: userDoc.name,
      };
    } else {
      let tabLeads = shouldRenderRows ? rowsBySalesStage[selectedTab] : [];
      if (selectedTab === ISDashboardTabs.PAYMENT_PENDING) {
        tabLeads = paymentPendingLeads;
      }

      extraProps = {
        userId: userDoc.id,
        userName: userDoc.name,
        setSelectedLead,
        coachesList,
        setShowQuestionsModal,
        onOnboardingLinkClick,
        onNotesClick,
        leads: tabLeads,
        selectedPeriod,
        setSelectedPeriod,
      };
    }

    return (
      <TabContent
        {...extraProps}
        {...props}
      />
    );
  }, [
    userDoc,
    coachesList,
    selectedTab,
    shouldRenderRows,
    rowsBySalesStage,
    setShowQuestionsModal,
    setSelectedLead,
    onOnboardingLinkClick,
    onNotesClick,
    selectedPeriod,
    setSelectedPeriod,
    paymentPendingLeads,
  ]);

  const onModalClose = useCallback(() => {
    setShowContractModal(false);
    setSelectedLead(null);
  }, [setShowContractModal, setSelectedLead]);

  return (
    <>
      <HeaderContainer>
        <TabHeaderRow>
          <StyledTabs
            tabsConfig={tabsConfigWithCounts}
            selectedTab={selectedTab}
            onSelectTab={setSelectedTab}
          />
        </TabHeaderRow>
      </HeaderContainer>
      <PageContent>
        {tabContent}
      </PageContent>
      {selectedLead && (
        <>
          <NotesModal
            lead={selectedLead}
            showModal={showNotesModal}
            onClose={() => setShowNotesModal(false)}
          />
          <LeadContractModal
            lead={selectedLead}
            coachId={selectedLead.coach}
            showModal={showContractModal}
            showPostCommitmentConfiguration
            onClose={onModalClose}
            isISView
          />
        </>
      )}
      <LoadingOverlay isLoading={loadingLeads || isLoading} />
    </>
  );
};

LeadsTableSection.propTypes = {
  setShowQuestionsModal: PropTypes.func.isRequired,
  coachesList: PropTypes.object.isRequired,
  setSelectedLead: PropTypes.func.isRequired,
  selectedLead: PropTypes.object,
};

LeadsTableSection.defaultProps = {
  selectedLead: null,
};

export default LeadsTableSection;
